import { useCallback, useState } from 'react';

import { HateoasTarget, ToolArgs, ToolsRegistry } from '@repo/contracts';

import { BinaryResponse, downloadBinaryResponse } from '@/components/module/candidate-sheet/dt/helpers/downloadBinary';


export interface UseToolExecutorProps {
    fetchLink: (link: HateoasTarget, body?: unknown, pathParams?: Record<string, string>, returnHeaders?: boolean) => Promise<Record<string, unknown> | undefined>;
}

const ACTION_NAME_TO_DT_REL: Record<string, string> = {
    'get_dts': 'dt:read',
    'dt_create_assistant': 'dt:create-assistant',
    'create_dt_with_ai_from_cv': 'dt:create-with-ai-from-cv',
    'create_dt_with_ai_from_dt': 'dt:create-with-ai-from-dt',
    'create_dt_with_ai_custom': 'dt:create-with-ai',
};

const GLOBAL_QUERY_KEYS = ['page', 'limit', 'filter', 'sort', 'search'];

export function useToolExecutor({ fetchLink }: UseToolExecutorProps) {
    const [executingToolLabel, setExecutingToolLabel] = useState<string | null>(null);

    const findToolDefinition = useCallback((toolName: string, tools: ToolsRegistry) => {
        const exactName = toolName;
        const legacyNameWithColon = ACTION_NAME_TO_DT_REL[toolName] ?? '';

        for (const componentId in tools) {
            if (tools[componentId]?.[exactName]) {
                return tools[componentId][exactName];
            } else if (tools[componentId]?.[legacyNameWithColon]) {
                return tools[componentId][legacyNameWithColon];
            }
        }
        console.error(`Outil "${toolName}" non disponible`);
        return null;
    }, []);

    /**
     * Sépare les arguments en 3 catégories : Path, Query et Body
     */
    const dispatchArguments = useCallback((href: string, method: string, args: ToolArgs) => {
        let resolvedHref = href;
        const queryParams = new URLSearchParams();
        const bodyParams: Record<string, unknown> = {};

        // 1. On détecte d'abord les paramètres présents dans le pattern de l'URL ({id} ou :id)
        const pathParamKeys: string[] = [];
        const matches = href.match(/\{(\w+)\}/g) || href.match(/:(\w+)/g) || [];
        matches.forEach((match) => {
            pathParamKeys.push(match.replace(/[{:}]/g, ''));
        });

        // 2. On boucle sur tous les arguments fournis par l'IA pour les dispatcher
        Object.entries(args).forEach(([key, value]) => {
            if (value == null) return;

            // Remplacement des variables de chemin (Ex: {id} ou :id)
            if (pathParamKeys.includes(key)) {
                resolvedHref = resolvedHref
                    .replace(`{${key}}`, String(value))
                    .replace(`:${key}`, String(value));
                return;
            }

            // Détermination si le champ va dans la Query String
            // - Si c'est un GET, tout va dans la Query par défaut
            // - Si c'est une clé universelle de Query (page, limit...)
            const isQueryKey = method === 'GET' || GLOBAL_QUERY_KEYS.includes(key.toLowerCase());

            if (isQueryKey) {
                queryParams.append(key, String(value));
            } else {
                bodyParams[key] = value;
            }
        });

        // 3. On reconstruit l'URL finale si des éléments de Query ont été accumulés
        const queryString = queryParams.toString();
        if (queryString) {
            // On gère le cas où l'URL originale contient déjà un '?'
            resolvedHref = resolvedHref.includes('?') 
                ? `${resolvedHref}&${queryString}` 
                : `${resolvedHref}?${queryString}`;
        }

        return {
            href: resolvedHref,
            body: Object.keys(bodyParams).length > 0 ? bodyParams : null,
        };
    }, []);

    const executeTool = useCallback(async (
        toolName: string,
        args: ToolArgs,
        tools: ToolsRegistry,
    ): Promise<{ success: boolean; result?: string }> => {
        const linkDef = findToolDefinition(toolName, tools);
        
        if (!linkDef?.href) {
            return { success: false, result: `Outil "${toolName}" non disponible` };
        }

        try {
            // Utilisation du dispatcher pour séparer dynamiquement les arguments
            const { href: resolvedHref, body } = dispatchArguments(
                linkDef.href, 
                linkDef.method || 'GET', 
                args,
            );
            
            // On exécute l'appel HTTP avec l'URL (contenant la Query) et le Body nettoyé
            setExecutingToolLabel(linkDef.label ?? null);
            const finalHref = resolvedHref.includes('?') ? `${resolvedHref}&fromChatbot=true` : `${resolvedHref}?fromChatbot=true`;
            
            const timeoutDuration = 5 * 60 * 1000; // 5 minutes
            const timeoutPromise = new Promise<never>((_, reject) => 
                setTimeout(() => reject(new Error('TIMEOUT')), timeoutDuration),
            );

            const response = await Promise.race([
                fetchLink({ ...linkDef, href: finalHref }, body, undefined, true),
                timeoutPromise,
            ]);
            const isBinary = (response?.headers as Record<string, string> | undefined)?.['content-disposition'] || response?.data instanceof Blob;

            if (isBinary) {
                const fileName = downloadBinaryResponse(response as BinaryResponse, { fallbackFileName: 'export-neylia' });
                return {
                    success: true,
                    result: `Le fichier ${fileName} a été généré et téléchargé avec succès sur l'ordinateur de l'utilisateur.`,
                };
            }

            setExecutingToolLabel(null);

            const method = linkDef.method || 'GET';
            if (method !== 'GET') {
                window.dispatchEvent(new CustomEvent('ai_mutation_success', { 
                    detail: { toolName }, 
                }));
            }

            return { success: true, result: JSON.stringify(response?.data) ?? 'Success' };
        } catch (error: unknown) {
            console.error('Erreur lors de l\'exécution de l\'outil:', error);
            setExecutingToolLabel(null);
            
            let safeMessage = `L'outil a échoué à cause d'un problème technique. Indique à l'utilisateur que l'action n'a pas pu aboutir.`;
            
            if (error instanceof Error && error.message === 'TIMEOUT') {
                safeMessage = `L'action a pris trop de temps (plus de 5 minutes) et a été annulée. Dis à l'utilisateur que le serveur met trop de temps à répondre.`;
            } else if (error && typeof error === 'object' && 'response' in error) {
                const errObj = error as { response?: { status?: number; data?: { message?: string | string[]; error?: string } } };
                const status = errObj.response?.status;
                
                if (typeof status === 'number' && status >= 400 && status < 500) {
                    const dataMessage = errObj.response?.data?.message || errObj.response?.data?.error;
                    if (dataMessage) {
                        safeMessage = `L'action a échoué (erreur de validation) : ${Array.isArray(dataMessage) ? dataMessage.join(', ') : dataMessage}. Explique ça poliment à l'utilisateur.`;
                    }
                }
            }

            return { success: false, result: safeMessage };
        }
    }, [findToolDefinition, dispatchArguments, fetchLink]);

    return { 
        executeTool,
        executingToolLabel,
        setExecutingToolLabel,
    };
}